/* eslint-disable react/jsx-no-duplicate-props */
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { OutlinedTextFieldProps, TextField } from '@mui/material';
import { usePhoneFormatter } from '@europrocurement/flexy-components/hooks/usePhoneFormatter';
import './inputs.css';

export type FlexyTextFieldProps = OutlinedTextFieldProps;

/**
 * Ce composant permet l'affichage d'un input au style personnalisé.
 *
 * ## Utilisation
 * Il hérite directement du [composant TextField de Material UI](https://mui.com/material-ui/react-text-field/), et s'utilise donc de la même façon.
 *
 * ## Fonctionnalités spéciales
 * - **Type "tel"** : formate automatiquement la saisie en numéro de téléphone
 *   - Supporte les formats nationaux (XX XX XX XX XX) et internationaux (+XX X XX XX XX XX)
 *   - Le formatage s'applique en temps réel pendant la saisie
 *   - Seuls les chiffres et le caractère + (en début) sont acceptés
 * - **Type "number"** : validation et outils numériques
 *   - Seuls les chiffres, point décimal, et signe moins sont autorisés
 *   - Support de la molette pour incrémenter/décrémenter
 *   - Flèches haut/bas natives pour navigation
 *   - Respect des propriétés min, max, step
 */
export const FlexyTextField: React.FunctionComponent<FlexyTextFieldProps> = styled(
    (props: FlexyTextFieldProps) => {
        const {
            InputProps: externalInputProps,
            inputProps: externalInputPropsNative,
            ...restProps
        } = props;
        const [internalValue, setInternalValue] = React.useState<string>(
            props.value?.toString() || props.defaultValue?.toString() || '',
        );

        const isPhoneType = props.type === 'tel';
        const isNumberType = props.type === 'number';

        const phoneFormatter = usePhoneFormatter(isPhoneType ? internalValue : undefined);

        // Helper pour valider les caractères numériques
        const isValidNumberCharacter = (
            key: string,
            currentValue: string,
            cursorPosition: number,
        ) => {
            // Touches de contrôle toujours autorisées
            if (
                [
                    'Backspace',
                    'Delete',
                    'Tab',
                    'Escape',
                    'Enter',
                    'Home',
                    'End',
                    'ArrowLeft',
                    'ArrowRight',
                    'ArrowUp',
                    'ArrowDown',
                ].includes(key)
            ) {
                return true;
            }

            // Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            if (key === 'a' || key === 'c' || key === 'v' || key === 'x') {
                return true;
            }

            // Chiffres
            if (/^[0-9]$/.test(key)) {
                return true;
            }

            // Point décimal (seulement un autorisé)
            if (key === '.' && !currentValue.includes('.')) {
                return true;
            }

            // Signe minus (seulement au début)
            if (key === '-' && cursorPosition === 0 && !currentValue.startsWith('-')) {
                return true;
            }

            return false;
        };

        // Helper pour valider le contenu collé
        const isValidNumberPaste = (pastedText: string) => {
            // Regex pour valider un nombre (entier ou décimal, positif ou négatif)
            const numberRegex = /^-?(\d+\.?\d*|\.\d+)$/;
            return numberRegex.test(pastedText.trim());
        };

        const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            setInternalValue(newValue);

            if (props.onChange) {
                props.onChange(event);
            }
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (isPhoneType) {
                const currentValue = (event.target as HTMLInputElement).value;
                const cursorPosition = (event.target as HTMLInputElement).selectionStart || 0;

                if (!phoneFormatter.isValidKey(event.key, cursorPosition, currentValue)) {
                    event.preventDefault();
                }
            }

            // Validation pour le type number
            if (isNumberType && !event.ctrlKey && !event.metaKey) {
                const currentValue = (event.target as HTMLInputElement).value;
                const cursorPosition = (event.target as HTMLInputElement).selectionStart || 0;

                if (!isValidNumberCharacter(event.key, currentValue, cursorPosition)) {
                    event.preventDefault();
                }
            }

            if (props.onKeyDown) {
                props.onKeyDown(event);
            }
        };

        const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
            if (isPhoneType) {
                const pastedText = event.clipboardData.getData('text');
                const currentValue = (event.target as HTMLInputElement).value;
                const cursorPosition = (event.target as HTMLInputElement).selectionStart || 0;

                if (!phoneFormatter.isValidPaste(pastedText, cursorPosition, currentValue)) {
                    event.preventDefault();
                    return;
                }
            }

            // Validation pour le type number
            if (isNumberType) {
                const pastedText = event.clipboardData.getData('text');
                if (!isValidNumberPaste(pastedText)) {
                    event.preventDefault();
                    return;
                }
            }

            if (props.onPaste) {
                props.onPaste(event);
            }
        };

        // 🎡 Gestion de la molette pour les champs number
        const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
            if (isNumberType && document.activeElement === event.target) {
                const input = event.target as HTMLInputElement;
                const currentValue = parseFloat(input.value) || 0;
                const step = parseFloat(props.inputProps?.step?.toString() || '1');
                const min = props.inputProps?.min
                    ? parseFloat(props.inputProps.min.toString())
                    : undefined;
                const max = props.inputProps?.max
                    ? parseFloat(props.inputProps.max.toString())
                    : undefined;

                let newValue = currentValue;

                // Molette vers le haut = incrément, vers le bas = décrément
                if (event.deltaY < 0) {
                    newValue = currentValue + step;
                } else {
                    newValue = currentValue - step;
                }

                // Respect des limites min/max
                if (min !== undefined && newValue < min) {
                    newValue = min;
                }
                if (max !== undefined && newValue > max) {
                    newValue = max;
                }

                // Mettre à jour la valeur
                const syntheticEvent = {
                    ...event,
                    target: { ...input, value: newValue.toString() },
                } as React.ChangeEvent<HTMLInputElement>;

                setInternalValue(newValue.toString());

                if (props.onChange) {
                    props.onChange(syntheticEvent);
                }

                event.preventDefault();
            }

            if (props.onWheel) {
                props.onWheel(event);
            }
        };

        React.useEffect(() => {
            if (props.value) {
                const stringValue = props.value.toString();
                setInternalValue(stringValue);
            }
        }, [props.value]);

        // Configuration des inputProps selon le type
        const getInputProps = () => {
            if (isPhoneType) {
                return {
                    ...externalInputPropsNative,
                    inputMode: 'tel' as const,
                    pattern: '[+]?[0-9 ]*',
                    maxLength: phoneFormatter.maxLength,
                };
            }

            if (isNumberType) {
                return {
                    ...externalInputPropsNative,
                    inputMode: 'decimal' as const,
                    pattern: '-?[0-9]*[.]?[0-9]*',
                };
            }

            return externalInputPropsNative;
        };

        const displayValue = isPhoneType ? phoneFormatter.formattedValue : internalValue;

        return (
            <TextField
                value={displayValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onWheel={handleWheel}
                inputProps={getInputProps()}
                InputProps={{
                    ...externalInputProps,
                    sx: {
                        ...externalInputProps?.sx,
                        // Style spécifique pour les inputs number
                        ...(isNumberType && {
                            '& input[type=number]': {
                                appearance: 'textfield',
                                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                    appearance: 'auto',
                                    opacity: 1,
                                },
                                '&:hover::-webkit-outer-spin-button, &:hover::-webkit-inner-spin-button':
                                    {
                                        opacity: 1,
                                    },
                            },
                        }),
                    },
                }}
                {...restProps}
            />
        );
    },
)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.background.paper,
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
    },
    '& .MuiOutlinedInput-input.Mui-disabled': {
        backgroundColor: 'transparent',
    },
    '& .MuiOutlinedInput-input.Mui-disabled::-webkit-input-placeholder': {
        color: theme.palette.text.secondary,
        opacity: '1',
    },
    '& input[type="number"]': {
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            opacity: 0.7,
            cursor: 'pointer',
        },
        '&:focus::-webkit-outer-spin-button, &:focus::-webkit-inner-spin-button': {
            opacity: 1,
        },
    },
}));
